import { httpClient } from '../http-client';
import { config } from '../config';
import type { WebSolution } from '@wix/ambassador-marketplace/types';
import type { SearchResponse } from '../types/common/marketplace';
import type { AppMarketRpcClient } from '../types/app-market-rpc-client';
import type {
  SearchResponse as SearchResponseinVespa,
  VersionStatus,
} from '@wix/ambassador-devcenter-ams-v1-app-market-search/types';
import type { GetPrivateAppsAccountResponse } from '@wix/ambassador-devcenter-v1-private-apps-account/types';
import { convertCurrency } from '@wix/ambassador-ecommerce-currency-converter-v1-currency-rate/http';

const AppHasEditorComponentsType = [
  'WEB',
  'CUSTOM_ELEMENT_WIDGET',
  'PLATFORM',
  'EDITOR_ADDON',
  'EDITOR_REACT_COMPONENT',
  'STUDIO',
  'STUDIO_WIDGET',
  'WIDGET',
  'WIDGET_OUT_OF_IFRAME',
  'PAGE',
  'PAGE_OUT_OF_IFRAME',
  'UNIFIED_PAGE',
];

export const getCurrencyConvert = (
  price: string,
  currency: string,
): Promise<string> => {
  const valueWithoutDecimal = price.replace(/\./g, '');
  const decimalIndex = price.indexOf('.') >= 0 ? price.indexOf('.') : 0;

  return httpClient
    .request(
      convertCurrency({
        amounts: [{ value: valueWithoutDecimal, decimalPlaces: decimalIndex }],
        from: 'USD',
        to: currency,
      }),
    )
    .then(({ data }) => {
      const priceResponse = data.amounts?.[0]?.value;
      const decimalPlacesResponse = data.amounts?.[0].decimalPlaces;
      if (priceResponse && decimalPlacesResponse) {
        const firstIndex = priceResponse.length - decimalPlacesResponse;
        const number = parseFloat(
          `${priceResponse.slice(0, firstIndex)}.${priceResponse.slice(
            firstIndex,
          )}`,
        ).toFixed(2);
        return number;
      }
      return price;
    });
};

const getPlansWithAdditionalChargesWithCurrency = async (
  webSolution: WebSolution,
) => {
  const currencySettings = webSolution.baseInfo?.pricing?.currencySettings;
  const updatedPlans = await Promise.all(
    webSolution.baseInfo?.pricing?.plans?.map(async (plan) => {
      const additionalChargesAmount =
        plan.meteredBilling?.checkoutAdditionalInfo
          ?.split('{')[1]
          ?.split('}')?.[0];
      if (additionalChargesAmount) {
        const additionalChargesWithCurrency = await getCurrencyConvert(
          additionalChargesAmount,
          currencySettings?.code ?? 'USD',
        );
        return {
          ...plan,
          additionalChargesWithCurrency,
        };
      } else {
        return plan;
      }
    }),
  );
  return updatedPlans;
};

export const getSolutionPreviewBySlugOrId = async (
  isPreview: boolean,
  slugOrId: string,
  locale: string,
  experiments: any,
  rpcClient?: AppMarketRpcClient,
): Promise<any> => {
  const isSSR = typeof window === 'undefined';
  const enableMeteredBilling = experiments.enabled(
    'spec.appMarket.meteredBilling',
  );
  if (!enableMeteredBilling) {
    if (isSSR && rpcClient !== undefined) {
      return rpcClient
        .getWebSolutionBySlug({
          idsOrSlugs: slugOrId,
          isPreview,
          lang: locale,
          isApproved: true,
        })
        .then((response) => response.webSolutions?.[0]);
    }

    const newApiURL = 'marketplace-api/v1';
    return httpClient
      .get(
        `${config.baseApiUrl}/_api/${newApiURL}/web-solutions/${slugOrId}?isPreview=${isPreview}&isApproved=true&lang=${locale}`,
      )
      .then(
        ({ data }) =>
          data.webSolutions &&
          data.webSolutions.length > 0 &&
          data.webSolutions[0],
      );
  } else {
    let webSolutionsResponse;
    if (isSSR && rpcClient !== undefined) {
      const { webSolutions } = await rpcClient.getWebSolutionBySlug({
        idsOrSlugs: slugOrId,
        isPreview,
        lang: locale,
        isApproved: true,
      });
      webSolutionsResponse = webSolutions;
    } else {
      const newApiURL = 'marketplace-api/v1';
      const {
        data: { webSolutions },
      } = await httpClient.get(
        `${config.baseApiUrl}/_api/${newApiURL}/web-solutions/${slugOrId}?isPreview=${isPreview}&isApproved=true&lang=${locale}`,
      );
      webSolutionsResponse = webSolutions;
    }
    if (webSolutionsResponse?.[0]) {
      const updatedPlans = await getPlansWithAdditionalChargesWithCurrency(
        webSolutionsResponse[0],
      );

      return {
        ...webSolutionsResponse[0],
        baseInfo: {
          ...webSolutionsResponse[0].baseInfo,
          pricing: {
            ...webSolutionsResponse[0].baseInfo.pricing,
            plans: updatedPlans,
          },
        },
      };
    } else {
      return null;
    }
  }
};

export const getSolutionsBaseBySearchQueryInVespa = (
  query: string,
  locale: string,
  apiLimit: number = 36,
  apiOffset: number = 0,
  rpcClient?: AppMarketRpcClient,
) => {
  const isSSR = typeof window === 'undefined';
  if (isSSR && rpcClient !== undefined) {
    return rpcClient
      .getAppsBySearchQueryInVespa({
        searchTerm: query,
        paging: { limit: apiLimit, offset: apiOffset },
      })
      .then((searchResponse) => ({
        suggestions: searchResponse.webSolutions,
        total: searchResponse.paging.total,
      }))
      .catch((error) => {
        return {
          suggestions: [],
          total: 0,
        };
      });
  }

  return httpClient
    .post<SearchResponseinVespa>(
      `${config.baseApiUrl}/app-market-search/v1/search`,
      {
        withCredentials: true,
        searchTerm: query,
        paging: { limit: apiLimit, offset: apiOffset },
      },
    )
    .then((searchResponse) => ({
      suggestions: searchResponse?.data?.webSolutions,
      total: searchResponse?.data.paging.total,
    }))
    .catch((error) => {
      console.error('cannot fetch apps from vespa', error);
      return {
        suggestions: [],
        total: 0,
      };
    });
};

export const getWixOfferingBySearchQueryInVespa = (
  query: string,
  locale: string,
  apiLimit: number = 36,
  apiOffset: number = 0,
  rpcClient?: AppMarketRpcClient,
): Promise<any> => {
  const isSSR = typeof window === 'undefined';
  if (isSSR && rpcClient !== undefined) {
    return rpcClient
      .getAppsBySearchQueryInVespa({
        searchTerm: query,
        paging: { limit: apiLimit, offset: apiOffset },
        filter: {
          $and: [
            {
              solution_type: {
                $eq: 'WIX_OFFERING',
              },
            },
            {
              status: {
                $eq: 'PUBLISHED',
              },
            },
          ],
        },
      })
      .then((searchResponse) => ({
        suggestions: searchResponse.webSolutions,
        total: searchResponse.paging.total,
      }))
      .catch(() => ({ suggestions: [], total: 0 }));
  }

  return httpClient
    .post<SearchResponseinVespa>(
      `${config.baseApiUrl}/app-market-search/v1/search`,
      {
        withCredentials: true,
        searchTerm: query,
        filter: {
          $and: [
            {
              solution_type: {
                $eq: 'WIX_OFFERING',
              },
            },
            {
              status: {
                $eq: 'PUBLISHED' as VersionStatus,
              },
            },
          ],
        },
        paging: { limit: apiLimit, offset: apiOffset },
      },
    )
    .then((searchResponse) => ({
      suggestions: searchResponse?.data?.webSolutions,
      total: searchResponse?.data.paging.total,
    }))
    .catch((error) => {
      console.error('cannot fetch Wix Offering from vespa', error);
      return {
        suggestions: [],
        total: 0,
      };
    });
};

export const getWixOfferingId = (appId: string) => {
  return httpClient
    .post(
      `${config.baseApiUrl}/_api/app-components-reader/v1/app-components-by-ids`,
      {
        withCredentials: true,
        componentTypes: ['WIX_OFFERING'],
        apps: [
          {
            appId,
          },
        ],
      },
    )
    .then((data) => data);
};

export const getAutocompleteAppsByQuery = (
  term: string,
  locale: string,
): Promise<SearchResponse> => {
  return httpClient
    .get(
      `${config.baseApiUrl}/_serverless/app-market-search/autocomplete?term=${term}&lang=${locale}`,
    )
    .then(({ data }) => data);
};

export const fetchNewInstalledApps = (signedInstance, local: string) => {
  if (signedInstance) {
    return httpClient
      .get(
        `${
          config.baseApiUrl
        }/_api/managed-apps/v1/managed-apps?lang=${local}&${AppHasEditorComponentsType.map(
          (type) => `componentType=${type}`,
        ).join('&')}`,
        {
          headers: { Authorization: signedInstance },
          withCredentials: true,
        },
      )
      .then((res) => res?.data?.managedApps)
      .catch((error) => console.log('cannot fetch installed apps', error));
  }
  return Promise.resolve([]);
};

export const fetchSiteData = (msid: string) => {
  if (msid) {
    return httpClient
      .get(
        `${config.baseApiUrl}/_serverless/app-market-rpc-proxy/premium-features/${msid}`,
      )
      .then((res) => {
        const result = res.data.body || {};
        const premiumFeatures = (result && result.premiumFeatures) || [];
        const domains = (result && result.domains) || [];

        return {
          siteIsPremium: !!premiumFeatures.length,
          siteHasDomain: !!domains.length,
        };
      })
      .catch((error) => {
        console.error('cannot fetch site data', error);
        return {
          siteIsPremium: true,
          siteHasDomain: true,
        };
      });
  }
  return {
    siteIsPremium: true,
    siteHasDomain: true,
  };
};

export const fetchBundleApps = (signedInstance: string) => {
  if (signedInstance) {
    return httpClient
      .get(
        `${config.baseApiUrl}/_api/marketplace-api/v1/bundle-apps?lang=en&includeDeleted=true`,
        {
          headers: { Authorization: signedInstance },
          withCredentials: true,
        },
      )
      .then((res) => res && res.data && res.data.bundleWebSolutions)
      .catch((error) => console.error('cannot fetch bundle apps', error));
  }
  return Promise.resolve([]);
};

export const fetchCurrentBundleApps = (signedInstance: string) => {
  if (signedInstance) {
    return httpClient
      .get(
        `${config.baseApiUrl}/_api/marketplace-api/v1/bundle-apps?lang=en&number_of_days=30`,
        {
          headers: { Authorization: signedInstance },
          withCredentials: true,
        },
      )
      .then((res) => res && res.data && res.data.bundleWebSolutions)
      .catch((error) =>
        console.error('cannot fetch current bundle apps', error),
      );
  }
  return Promise.resolve([]);
};

export const postDeepLinkEditorCache = (metaSiteId: string, data: any) => {
  if (metaSiteId) {
    return httpClient
      .post(
        `${config.baseApiUrl}/_serverless/deep-link-editor-cache/data`,
        data,
      )
      .catch((error) =>
        console.error('cannot post deep link editor cache', error),
      );
  }
  return Promise.resolve([]);
};

export const getPrivateAppsAccount = (
  rpcClient?: AppMarketRpcClient,
): Promise<GetPrivateAppsAccountResponse> => {
  const isSSR = typeof window === 'undefined';
  if (isSSR && rpcClient !== undefined) {
    return rpcClient.getPrivateAppsAccount({}).catch((error) => {
      console.error('cannot fetch account', error);
      return {};
    });
  }

  return httpClient
    .get<GetPrivateAppsAccountResponse>(
      `${config.baseApiUrl}/_api/private-apps/v1/account`,
      { withCredentials: true },
    )
    .then(({ data }) => data)
    .catch((error) => {
      console.error('cannot fetch private apps', error);
      return {};
    });
};

export const getPrivateAppsTagId = async (rpcClient?: AppMarketRpcClient) => {
  const account = await getPrivateAppsAccount(rpcClient);
  return account?.account?.tagId;
};
